import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';
import shortid from 'shortid';
import { cloneDeep } from 'lodash';
import {
  Form,
  FormInstance,
  DateSerialized,
  TextInput,
  Checkbox,
  SelectBooleanInput
} from '@lightbeamhealth/blueprint-form';
import { faPlusCircle, faTimesCircle } from '@fortawesome/pro-solid-svg-icons';

import Icon from '../../../atoms/icon';
import Button from '../../../molecules/button';
import { Row, Col } from '../../../../grid';
import { StartDate } from '../date';
import { EndDate } from '../date';
import SelectMethod from '../method';
import SelectTime from '../time';
import Weekday from '../weekday';
import Timezone from '../timezone';
import DayOfMonth from '../day';
import ScheduleRepeat from '../repeat';
import { CardBlock, CardBreakout, CardFrame } from '../../card';
import Callout from '../../callout';
import Toast from '../../toast';
import { SimpleSelect } from '../../select';
import { GetCurrentTZ } from '../../../../libs/time';
import { Module, IEpxPedOncSchedule } from '../../../../libs/apps/epxpedonc';
import { useScheduleForm } from '../../../../contexts/form';
import { useMetaData } from '../../../../contexts/metadata';

interface EpxPedOncModuleData {
  uid: string;
  name: string;
  start: string;
  end?: string;
  times?: string[]; // Array of 'HH:mm' strings for multiple check-ins per day
  type?: string;
  step: number;
  days?: string;
  isDiscontinued?: boolean;
  _isNew?: boolean;
}

type EpxPedOncModuleDataRaw = Omit<
  DateSerialized<EpxPedOncModuleData, 'start' | 'end'>,
  'days' | 'times'
> & {
  times: string[]; // Array of 'HH:mm' strings for multiple check-ins per day
  days?: number[];
  isDiscontinued?: boolean;
};

interface EpxPedOncData {
  method: string;
  language: string;
  start: string;
  end?: string;
  tz: string;
  medications: EpxPedOncModuleData[];
  housingDay?: number;
  checkup?: boolean;
  isReminderOnly?: boolean;
  continueIfBetter?: boolean;
}

type EpxPedOncDataRaw = Omit<
  DateSerialized<EpxPedOncData, 'start' | 'end'>,
  'medications'
> & {
  medications?: EpxPedOncModuleDataRaw[];
};

type EpxPedOncEditData = Omit<EpxPedOncData, 'medications'> & {
  medications?: {
    add?: EpxPedOncModuleData[];
    edit?: EpxPedOncModuleData[];
    remove?: string[];
  };
};

const getNewMedication = (): EpxPedOncModuleData => {
  return {
    uid: shortid.generate(),
    name: '',
    start: moment().startOf('day').format(),
    end: null,
    times: ['08:00'], // Default to one check-in time
    type: 'weekly',
    step: 1,
    days: '',
    _isNew: true
  };
};

const createMedicationOptions = (medications: EpxPedOncModuleDataRaw[]) => {
  return medications
    .sort(
      (a: any, b: any) => Number(a.isDiscontinued) - Number(b.isDiscontinued)
    )
    .map((medication, index) => {
      let label = `${index + 1} - ${
        medication.name === '' ? '[Medication Name]' : medication.name
      }`;
      if (medication.isDiscontinued) {
        label += ' (Discontinued)';
      }
      return {
        label,
        value: medication.uid
      };
    });
};

export const EpxPedOncDefaultData: EpxPedOncData = {
  method: 'message',
  language: 'en',
  start: moment().startOf('day').format(),
  end: null,
  tz: GetCurrentTZ(),
  medications: [getNewMedication()],
  isReminderOnly: false,
  checkup: false,
  housingDay: null,
  continueIfBetter: false
};

const SerializeDateTime = (date: Date, time: string, tz: string): string => {
  if (!date) {
    return null;
  }
  const timestamp = moment.tz(date, tz);
  const parsed = moment(time, 'HH:mm');
  timestamp.hour(parsed.hour()).minute(parsed.minute()).startOf('minute');
  return timestamp.format();
};

const DeserializeDateTime = (timestamp: string, tz: string): [Date, string] => {
  if (!timestamp) {
    return [null, null];
  }
  const parsed = moment.tz(timestamp, tz);
  return [parsed.toDate(), parsed.format('HH:mm')];
};

interface EpxPedOncMedFormProps {
  index: number;
  medication: EpxPedOncModuleDataRaw;
  onRemoveMedication: (uid: string) => void;
  onAddTime: (medUid: string) => void;
  onRemoveTime: (medUid: string, timeIndex: number) => void;
}

const EpxPedOncMedForm: React.FC<EpxPedOncMedFormProps> = ({
  index,
  medication,
  onRemoveMedication,
  onAddTime,
  onRemoveTime
}) => {
  if (!medication) {
    return null;
  }
  const prefix = `medications[${index}]`;
  const name =
    medication.name.length === 0 ? '[Medication Name]' : medication.name;
  const times = medication.times || ['08:00'];
  const discontinued = medication.isDiscontinued;
  return (
    <CardFrame>
      {medication.isDiscontinued && (
        <CardBlock>
          <Callout intent="primary" title="Discontinued">
            <span className="mt-4">
              This medication has been discontinued and it is no longer
              editable. You can click "Remove Medication" to remove it from the
              prescription. If there is only one medication for the patient's
              prescription and it is discontinued, you will have to add a new
              medication first before removing the discontinued one.
            </span>
          </Callout>
        </CardBlock>
      )}
      <CardBlock>
        <Row>
          <Col sm={24}>
            <TextInput
              label="Medication Name"
              name={`${prefix}.name`}
              fill
              required
              disabled={discontinued}
            />
          </Col>
        </Row>
        <Callout intent="primary" title="Example Message(s)">
          <span className="mt-4">
            Did you take your <b>{name}</b> at {times[0]}? Reply YES or NO.
          </span>
        </Callout>
      </CardBlock>
      <CardBlock>
        <Row>
          <Col sm={12}>
            <StartDate
              label="Start Date"
              name={`${prefix}.start`}
              ignoreEditMode={medication._isNew}
              fill
              disabled={discontinued}
            />
          </Col>
          <Col sm={12}>
            <EndDate
              label="End Date"
              name={`${prefix}.end`}
              disabled={discontinued}
            />
          </Col>
        </Row>
      </CardBlock>
      <CardBlock>
        <label className="bp5-label">Check-in Times</label>
        {times.map((time, timeIdx) => (
          <Row key={timeIdx} className="mb-2">
            <Col sm={16}>
              <SelectTime
                label=""
                name={`${prefix}.times[${timeIdx}]`}
                disabled={discontinued}
              />
            </Col>
            <Col sm={8} className="d-flex align-items-end pb-2">
              {times.length > 1 && (
                <Button
                  intent="danger"
                  minimal
                  onClick={() => onRemoveTime(medication.uid, timeIdx)}
                  disabled={discontinued}
                >
                  <Icon icon={faTimesCircle} />
                </Button>
              )}
            </Col>
          </Row>
        ))}
        <Button
          intent="primary"
          minimal
          onClick={() => onAddTime(medication.uid)}
          disabled={discontinued}
        >
          <Icon icon={faPlusCircle} className="mr-2" /> Add Another Check-in
          Time
        </Button>
      </CardBlock>
      <CardBlock>
        <Row>
          <Col sm={12}>
            <SelectBooleanInput
              label="Discontinued?"
              name="isDiscontinued"
              trueLabel="Yes"
              falseLabel="No"
              fill
              disabled
            />
          </Col>
        </Row>
      </CardBlock>
      <CardBlock>
        <ScheduleRepeat
          label="Repeat Medication"
          names={{
            mode: `${prefix}.type`,
            step: `${prefix}.step`
          }}
          modes={{
            day: 'daily',
            week: 'weekly'
          }}
          disabled={discontinued}
        />
      </CardBlock>
      {medication.type === 'weekly' ? (
        <CardBreakout>
          <Weekday
            name={`${prefix}.days`}
            minItems={1}
            inline
            disabled={discontinued}
          />
        </CardBreakout>
      ) : null}
      <CardBlock>
        <Button
          intent="danger"
          onClick={() => onRemoveMedication(medication.uid)}
          disabled={discontinued}
        >
          <Icon icon={faTimesCircle} className="mr-2" /> Remove Medication
        </Button>
      </CardBlock>
    </CardFrame>
  );
};

interface EpxPedOncFormProps {
  setForm?: (form: FormInstance<EpxPedOncDataRaw>) => void;
  onSubmit?: (data: EpxPedOncData | EpxPedOncEditData) => void;
  rawSchedule?: IEpxPedOncSchedule;
  isDisabled?: boolean;
}

const EpxPedOncForm = ({
  setForm,
  onSubmit: _onSubmit,
  rawSchedule,
  isDisabled
}: EpxPedOncFormProps): JSX.Element => {
  const { terms } = useMetaData();
  const { isEditMode } = useScheduleForm();
  const serialize = (data: EpxPedOncDataRaw): EpxPedOncData => {
    const medications: EpxPedOncModuleData[] = [];
    let start: Date = null;
    let end: Date = null;
    let hasNoEnd = false;
    for (const entry of data.medications) {
      if (!start || entry.start < start) {
        start = entry.start;
      }
      if (!entry.end) {
        hasNoEnd = true;
      } else if (!end || entry.end > end) {
        end = entry.end;
      }
      // Sort times chronologically
      const sortedTimes = [...(entry.times || ['08:00'])].sort(
        (a, b) => moment(a, 'HH:mm').valueOf() - moment(b, 'HH:mm').valueOf()
      );
      const medication: EpxPedOncModuleData = {
        ...entry,
        start: SerializeDateTime(
          entry.start,
          sortedTimes[0] || '08:00',
          data.tz
        ),
        end: SerializeDateTime(entry.end, '23:59', data.tz),
        times: sortedTimes,
        days: entry.days && entry.days.length > 0 ? entry.days.join(',') : null
      };
      // Remove legacy 'time' field if it exists to prevent data inconsistencies
      delete (medication as any).time;
      medications.push(medication);
    }
    if (hasNoEnd) {
      end = null;
    }
    return {
      ...data,
      start: SerializeDateTime(start, '00:00', data.tz),
      end: SerializeDateTime(end, '23:59', data.tz),
      medications,
      housingDay: data.housingDay === -1 ? null : data.housingDay
    };
  };
  const deserialize = (data: EpxPedOncData): EpxPedOncDataRaw => {
    const medications: EpxPedOncModuleDataRaw[] = [];
    for (const entry of data.medications) {
      const [start, time] = DeserializeDateTime(entry.start, data.tz);
      const [end, _] = DeserializeDateTime(entry.end, data.tz);
      // Use times array if available, otherwise derive from start time
      const times =
        entry.times && entry.times.length > 0
          ? entry.times
          : time
            ? [time]
            : ['08:00'];
      medications.push({
        ...entry,
        start,
        end,
        times,
        days: entry.days
          ? entry.days.split(',').map((day) => parseInt(day))
          : []
      });
    }
    return {
      ...data,
      start: DeserializeDateTime(data.start, data.tz)[0],
      end: DeserializeDateTime(data.end, data.tz)[0],
      medications,
      housingDay: data.housingDay != null ? data.housingDay : -1
    };
  };
  const defaultData = deserialize(EpxPedOncDefaultData);
  const [currentMed, setCurrentMed] = useState<string>(
    defaultData.medications[0].uid
  );
  const form = new FormInstance<EpxPedOncDataRaw, EpxPedOncData>(defaultData, {
    serialize,
    deserialize
  });
  useEffect(() => {
    if (setForm) {
      setForm(form);
    }
  }, []);
  const onSubmit = (data: EpxPedOncData): void => {
    if (isEditMode) {
      // Compare the medication list
      const medications: EpxPedOncEditData['medications'] = {
        add: [],
        edit: [],
        remove: []
      };
      const originalMedsByUID: Record<string, Module> = {};
      for (const entry of rawSchedule.medications) {
        if (!entry.isDiscontinued) {
          originalMedsByUID[entry.uid] = entry;
        }
      }
      const newMedsByUID: Record<string, EpxPedOncModuleData> = {};
      for (const entry of data.medications) {
        if (entry.isDiscontinued) {
          continue;
        }
        newMedsByUID[entry.uid] = entry;
        if (!originalMedsByUID[entry.uid]) {
          medications.add.push(entry);
        } else {
          medications.edit.push(entry);
        }
      }
      for (const entry of rawSchedule.medications) {
        if (!entry.isDiscontinued && !newMedsByUID[entry.uid]) {
          medications.remove.push(entry.uid);
        }
      }
      _onSubmit({
        ...data,
        medications
      });
    } else {
      const _data = cloneDeep(data);
      for (const medication of _data.medications) {
        delete (medication as any)._isNew;
        delete (medication as any).time;
      }
      _onSubmit(_data);
    }
  };
  // Medication operations
  const [tempData, setTempData] = useState<EpxPedOncData>(EpxPedOncDefaultData);
  const onChange = (data: EpxPedOncData) => {
    setTempData(data);
  };
  const onAddMedication = () => {
    const data = { ...tempData };
    const medication = getNewMedication();
    data.medications.push(medication);
    form.setData(data);
    setCurrentMed(medication.uid);
  };
  const onRemoveMedication = (uid: string) => {
    const medications: EpxPedOncModuleData[] = [];
    for (const entry of tempData.medications) {
      if (entry.uid !== uid) {
        medications.push(entry);
      }
    }
    if (medications.length === 0) {
      Toast({
        intent: 'danger',
        message: 'There must be at least 1 medication in the schedule!',
        timeout: 3000
      });
      return;
    }
    const data = { ...tempData, medications };
    form.setData(data);
    setCurrentMed(data.medications[0].uid);
  };
  const onAddTime = (medUid: string) => {
    const data = cloneDeep(tempData);
    for (const medication of data.medications) {
      if (medication.uid === medUid) {
        if (!medication.times) {
          medication.times = ['08:00'];
        }
        // Add a new time, default to 12:00 or next available hour
        const existingHours = medication.times.map((t) =>
          moment(t, 'HH:mm').hour()
        );
        let newHour = 12;
        while (existingHours.includes(newHour) && newHour < 23) {
          newHour++;
        }
        medication.times.push(`${String(newHour).padStart(2, '0')}:00`);
        break;
      }
    }
    form.setData(data);
  };
  const onRemoveTime = (medUid: string, timeIndex: number) => {
    const data = cloneDeep(tempData);
    for (const medication of data.medications) {
      if (medication.uid === medUid) {
        if (medication.times && medication.times.length > 1) {
          medication.times.splice(timeIndex, 1);
        } else {
          Toast({
            intent: 'danger',
            message: 'There must be at least 1 check-in time!',
            timeout: 3000
          });
        }
        break;
      }
    }
    form.setData(data);
  };
  useEffect(() => {
    if (!tempData) {
      return;
    }
    let isCardBlank = true;
    for (const entry of tempData.medications) {
      if (entry.uid === currentMed) {
        isCardBlank = false;
        break;
      }
    }
    if (isCardBlank) {
      setCurrentMed(tempData.medications[0].uid);
    }
  }, [tempData]);
  return (
    <CardBlock bare>
      <Form
        form={form}
        onSubmit={onSubmit}
        onChange={onChange}
        disabled={isDisabled}
      >
        {(props) => {
          return (
            <>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <SelectMethod />
                  </Col>
                  <Col sm={12}>
                    <label className="bp5-label">Mode</label>
                    <Checkbox
                      label="Medication Reminder Only"
                      name="isReminderOnly"
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={12}>
                    <Timezone label="Timezone" name="tz" fill />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Button intent="primary" onClick={onAddMedication}>
                  <Icon icon={faPlusCircle} className="mr-2" /> Add Another
                  Medication
                </Button>
              </CardBlock>
              <CardBlock>
                <SimpleSelect
                  aria-label="Select Medication"
                  className="mb-6"
                  options={createMedicationOptions(props.values.medications)}
                  onChange={(evt) => setCurrentMed(evt.target.value)}
                  value={currentMed}
                  fill
                />
                <EpxPedOncMedForm
                  index={props.values.medications.findIndex(
                    (medication) => medication.uid === currentMed
                  )}
                  medication={props.values.medications.find(
                    (medication) => medication.uid === currentMed
                  )}
                  onRemoveMedication={onRemoveMedication}
                  onAddTime={onAddTime}
                  onRemoveTime={onRemoveTime}
                />
              </CardBlock>
              <CardBlock>
                <b>Optional Modules</b>
                <Row className="mt-6">
                  <Col sm={12}>
                    <DayOfMonth
                      label="Ask about Housing At"
                      name="housingDay"
                      hasNever
                      hasExtraDays
                      fill
                    />
                  </Col>
                  <Col sm={12}>
                    <SelectBooleanInput
                      label="Checkup Message"
                      name="checkup"
                      fill
                    />
                  </Col>
                </Row>
              </CardBlock>
              <CardBlock>
                <Row>
                  <Col sm={24}>
                    <SelectBooleanInput
                      label="Contine if Feeling Better"
                      name="continueIfBetter"
                      trueLabel="Yes"
                      falseLabel="No"
                      fill
                    />
                    <Callout className="mt-4">
                      If yes, the {terms.patient.default} will be reminded to
                      continue taking the medication, if the patient responds
                      'Felt Better' when asked why they missed a dose of their
                      medication.
                    </Callout>
                  </Col>
                </Row>
              </CardBlock>
            </>
          );
        }}
      </Form>
    </CardBlock>
  );
};

export default EpxPedOncForm;
